import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import {
  nodesShareTag,
  nodeHasTag,
  CURVE_STROKE_WIDTH,
  FADED_NODE_OPACITY,
  getCurveStyle,
  NODE_HIGHLIGHT_OPACITY,
  NODE_OPACITY,
  NODE_RADIUS,
  SELECTED_NODE_RADIUS,
  generateNodesAndLinks,
} from "./utils";

const drawNetworkOutline = (
  g,
  networkLabelsGroup,
  nodes,
  label,
  conversationNames,
  showConversationLabels
) => {
  // Remove existing outlines and labels
  g.selectAll(`.network-outline-${label}`).remove();
  networkLabelsGroup.selectAll(`.network-label-${label}`).remove();

  // Outline less than 3 nodes
  if (nodes.length <= 2) {
    // Calculate the midpoint
    const x1 = nodes[0].x, y1 = nodes[0].y;
    const x2 = nodes[1].x, y2 = nodes[1].y;
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    // Distance between the two nodes
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.sqrt(dx * dx + dy * dy);
    // Draw an ellipse that covers both nodes
    g.append("ellipse")
      .attr("class", `network-outline network-outline-${label}`)
      .attr("cx", midX)
      .attr("cy", midY)
      .attr("rx", dist / 2 + 12) // Reduced padding
      .attr("ry", 16) // Smaller vertical radius
      .attr("fill", "white")
      .attr("fill-opacity", 0.15)
      .attr("transform", `rotate(${(Math.atan2(dy, dx) * 180) / Math.PI},${midX},${midY})`);
    return;
  }
  // Calculate the convex hull of the nodes
  const hull = d3.polygonHull(nodes.map((d) => [d.x, d.y]));

  if (hull) {
    // Calculate the centroid of the hull
    const centroid = d3.polygonCentroid(hull);
    const expansionFactor = 20; // Amount by which to expand the hull

    // Expand the convex hull outward from the centroid
    const expandedHull = hull.map(([x, y]) => {
      const [cx, cy] = centroid;
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scale = (dist + expansionFactor) / dist;
      return [cx + dx * scale, cy + dy * scale];
    });

    // Create a smooth closed curve around the expanded convex hull
    const outlinePathGenerator = d3
      .line()
      .x((d) => d[0])
      .y((d) => d[1])
      .curve(d3.curveBasisClosed);

    g.append("path")
      .datum(expandedHull)
      .attr("class", `network-outline network-outline-${label}`)
      .attr("d", outlinePathGenerator)
      .attr("fill", "white")
      .attr("fill-opacity", 0.15);

    if (showConversationLabels) {
      // Add the label at the centroid of the expanded hull
      const networkName = conversationNames
        ? conversationNames[nodes[0].conversationId] ??
          `Conversation ${nodes[0].conversationId}`
        : `Conversation ${nodes[0].conversationId}`;

      // Top of the hull
      const top = expandedHull.reduce((acc, cur) =>
        acc[1] > cur[1] ? cur : acc
      );

      const networkNameSplit = networkName.split("\n");
      // Iterate over the split network name and add it to the label
      networkNameSplit.forEach((token, index) => {
        networkLabelsGroup
          .append("text")
          .attr("class", `network-label network-label-${label}`)
          .attr("x", centroid[0])
          .attr("y", top[1] + index * 15 - (networkNameSplit.length - 1) * 10)
          .attr("text-anchor", "middle")
          .attr("fill", "white")
          .attr("opacity", 0.8)
          .attr("font-size", "10px")
          .attr("font-weight", "bold")
          .text(token);
      });
    }
  }
};

const initializeNodePositions = (nodes, width, height) => {
  const centralNodes = nodes.filter((d) => /^Subnetwork-\d+$/.test(d.id));
  const gridSize = Math.ceil(Math.sqrt(centralNodes.length));
  const spacingX = width / (gridSize + 1);
  const spacingY = height / (gridSize + 1);

  centralNodes.forEach((node, index) => {
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;
    node.x = spacingX * (col + 1);
    node.y = spacingY * (row + 1);

    // Place subnetwork nodes around their central node
    const subnetworkNodes = nodes.filter((d) =>
      d.id.startsWith(`${node.id} - Node`)
    );
    subnetworkNodes.forEach((subNode, subIndex) => {
      const angle = (2 * Math.PI * subIndex) / subnetworkNodes.length;
      const radius = 50; // Radius around the central node
      subNode.x = node.x + radius * Math.cos(angle);
      subNode.y = node.y + radius * Math.sin(angle);
    });
  });

  // Position the central node in the center
  const centralNode = nodes.find((d) => d.id === "Central Node");
  if (centralNode) {
    centralNode.x = width / 2;
    centralNode.y = height / 2;
  }
};

const showConnections = (
  node,
  data,
  gCurves,
  nodes,
  selectedTagRef,
  simulationIDRef
) => {
  // Increment simulation ID to cancel previous simulations
  simulationIDRef.current += 1;
  const currentSimulationID = simulationIDRef.current;

  const targetNodes = data.nodes.filter(
    (n) => n.highlight && nodesShareTag(node, n)
  );

  if (targetNodes.length !== 0) {
      drawCurvedLines(
      gCurves,
      node,
      targetNodes,
      selectedTagRef,
      simulationIDRef,
      currentSimulationID
    );
  } else {
    // Clear existing curves
    gCurves.selectAll(".curved-lines").remove();
  }

  // Enlarge the current node and its connected nodes, fade others
  nodes
    .transition()
    .duration(500)
    .attr("r", (n) =>
      n.id === node.id || targetNodes.includes(n)
        ? SELECTED_NODE_RADIUS
        : NODE_RADIUS
    )
    .attr("opacity", (n) =>
      n.id === node.id || targetNodes.includes(n)
        ? nodeHasTag(n, selectedTagRef.current)
          ? NODE_OPACITY
          : FADED_NODE_OPACITY
        : FADED_NODE_OPACITY
    );
};

const drawCurvedLines = (
  svg,
  sourceNode,
  targetNodes,
  selectedTagRef,
  simulationIDRef,
  simulationID
) => {
  // Clear existing curves
  svg.selectAll(".curved-lines").remove();

  // Parameters for the simulation
  const MIN_SUBDIVISIONS = 2;
  const MAX_SUBDIVISIONS = 6;
  const K = 12; // Spring constant - higher values make the curves more elastic
  const S = 0.25; // Damping factor - higher values make the curves less oscillatory
  const P = 0.08; // Stiffness factor - higher values make the curves more stiff
  const maxIterations = 5;
  const NEIGHBOR_RADIUS_SQ = 625; // Square of the neighbor radius (5x5 grid)
  const SHORT_EDGE_THRESHOLD = 550; // px, adjust as needed. 600px is the lowest rounded value that consistently connects nodes with the current tapering calculation.
  const TAPER_STRENGTH = 4; //Exponent for taper strength.

  // Find the minimum and maximum edge lengths
  let minEdgeLength = Infinity;
  let maxEdgeLength = 0;

  // Calculate edge lengths and initialize edges
  const edges = targetNodes.map((targetNode) => {
    const dx = targetNode.x - sourceNode.x;
    const dy = targetNode.y - sourceNode.y;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length < minEdgeLength) minEdgeLength = length;
    if (length > maxEdgeLength) maxEdgeLength = length;

    return {
      source: sourceNode,
      target: targetNode,
      length: length,
      points: [],
      sourceNode,
      targetNode,
    };
  });

  // Function to get subdivisions based on edge length
  function getSubdivisions(edgeLength) {
    if (maxEdgeLength === minEdgeLength) return MIN_SUBDIVISIONS;
    // Normalize edge length to a value between 0 and 1
    const normalizedLength =
      (edgeLength - minEdgeLength) / (maxEdgeLength - minEdgeLength);

    // Map normalized length to subdivisions
    const subdivisions =
      MIN_SUBDIVISIONS +
      normalizedLength * (MAX_SUBDIVISIONS - MIN_SUBDIVISIONS);

    return Math.round(subdivisions);
  }

  // Initialize edges with straight lines from source to target
  edges.forEach((edge) => {
    const numSubdivisions = getSubdivisions(edge.length);
    const points = [];

    for (let i = 0; i <= numSubdivisions + 1; i++) {
      const t = i / (numSubdivisions + 1);
      const x = edge.source.x * (1 - t) + edge.target.x * t;
      const y = edge.source.y * (1 - t) + edge.target.y * t;
      points.push({
        x,
        y,
        ox: x,
        oy: y,
        edge, // the parent edge
        pointIndex: i, // the index of the point in the edge
        numPoints: numSubdivisions + 2 // the number of points in the edge
      });
    }
    edge.points = points;
  });

  const lineGenerator = d3
    .line()
    .curve(d3.curveBasis)
    .x((d) => d.x)
    .y((d) => d.y);

  const edgePaths = svg
    .append("g")
    .attr("class", "curved-lines")
    .selectAll("path")
    .data(edges)
    .enter()
    .append("path")
    .attr("d", (d) => lineGenerator(d.points))
    .attr("stroke", (d) => getCurveStyle(d, selectedTagRef.current).stroke)
    .attr("stroke-width", (d) =>
      selectedTagRef.current
        ? getCurveStyle(d, selectedTagRef.current).strokeWidth
        : CURVE_STROKE_WIDTH
    )
    .attr("fill", "none")
    .attr("opacity", (d) => getCurveStyle(d, selectedTagRef.current).opacity)
    .style("stroke-dasharray", function () {
      const length = this.getTotalLength();
      return `${length} ${length}`;
    })
    .style("stroke-dashoffset", function () {
      return this.getTotalLength();
    });

  let iteration = 0;
  while (iteration < maxIterations) {
    runSimulationStep();
  }

  const finalPositions = edges.map((edge) => ({
    points: edge.points.map((p) => ({ x: p.x, y: p.y })),
  }));

  edges.forEach((edge, i) => {
    const numSubdivisions = getSubdivisions(edge.length);
    for (let j = 0; j <= numSubdivisions + 1; j++) {
      const t = j / (numSubdivisions + 1);
      edge.points[j].x = edge.source.x * (1 - t) + edge.target.x * t;
      edge.points[j].y = edge.source.y * (1 - t) + edge.target.y * t;
    }
  });

  edgePaths.attr("d", (d) => lineGenerator(d.points));
  // grow the straight lines
  edgePaths
    .transition()
    .duration(400)
    .ease(d3.easeQuadOut)
    .style("stroke-dashoffset", 0)
    .end()
    .then(() => {
      // animate to bundled positions after the edges are grown
      edges.forEach((edge, i) => {
        edge.points = finalPositions[i].points;
      });

      edgePaths
        .transition()
        .duration(400)
        .ease(d3.easeQuadInOut)
        .attr("d", (d) => lineGenerator(d.points));
    })
    .catch((err) => {
      // This prevents Uncaught (in promise) errors when transitions are interrupted
      if (err && err.code !== 0) { // code 0 is 'transition interrupted'
        console.error("Error in edgePaths transition:", err);
      }
    });

  function runSimulationStep() {
    iteration++;

    // Check if the simulation has been canceled
    if (simulationID !== simulationIDRef.current) {
      return;
    }

    // Collect all control points excluding endpoints
    const points = edges.flatMap((edge) => edge.points.slice(1, -1));

    // Create spatial hash grid
    const cellSize = Math.sqrt(NEIGHBOR_RADIUS_SQ);
    const grid = new Map();

    // Add points to grid
    points.forEach((point, index) => {
      const cellX = Math.floor(point.x / cellSize);
      const cellY = Math.floor(point.y / cellSize);
      const key = `${cellX},${cellY}`;
      if (!grid.has(key)) {
        grid.set(key, []);
      }
      grid.get(key).push({ point, index });
    });

    // Apply forces using grid
    points.forEach((point, i) => {
      const cellX = Math.floor(point.x / cellSize);
      const cellY = Math.floor(point.y / cellSize);

      // Pre-calculate force constants
      const ks = K * S;
      const ps = P * S;

      // Track cumulative forces
      let fx = 0;
      let fy = 0;
      let numNeighbors = 0;

      // Check neighboring cells
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const key = `${cellX + dx},${cellY + dy}`;
          const cell = grid.get(key);
          if (!cell) continue;

          // Apply forces from points in this cell
          cell.forEach(({ point: other, index }) => {
            if (index === i) return;

            const dx = other.x - point.x;
            const dy = other.y - point.y;
            const distSq = dx * dx + dy * dy;

            if (distSq > 0 && distSq < NEIGHBOR_RADIUS_SQ) {
              // Stronger force for closer points
              const force = ks / (Math.sqrt(distSq) + 1);
              fx += force * dx;
              fy += force * dy;
              numNeighbors++;
            }
          });
        }
      }

      // Scale forces based on number of neighbors
      if (numNeighbors > 0) {
        const scale = 1 / Math.sqrt(numNeighbors);
        fx *= scale;
        fy *= scale;
      }

      // --- Tapering: reduce bundling force near endpoints ---
      let taper = 1;
      if (
        point.pointIndex > 0 &&
        point.pointIndex < point.numPoints - 1
      ) {
        const t = point.pointIndex / (point.numPoints - 1); // 0 at source, 1 at target
        if (point.edge.length < SHORT_EDGE_THRESHOLD) {
          // Strong taper for short edges
          taper = Math.pow(Math.sin(Math.PI * t), TAPER_STRENGTH); 
        } else {
          // No taper for long edges
          taper = 1;
        }
      }
      fx *= taper;
      fy *= taper;
      // --- End tapering ---

      // Apply accumulated forces
      point.x += fx;
      point.y += fy;

      // Move towards original position
      const dx = point.ox - point.x;
      const dy = point.oy - point.y;
      const distToOriginal = Math.sqrt(dx * dx + dy * dy);

      // Stronger attraction when far from original position
      const attractionScale = Math.min(1, distToOriginal / 50);
      point.x += dx * ps * attractionScale;
      point.y += dy * ps * attractionScale;
    });
  }
};

// Custom force to pull nodes towards their subnetwork centers
const forcePullToSubnetworkCenter = (nodes, strength, width, height) => {
  const nodeById = new Map(nodes.map((d) => [d.id, d]));
  const widthHeightRatio = height / width;
  return (alpha) => {
    nodes.forEach((d) => {
      if (d.id.startsWith("Subnetwork-")) {
        const centralNode = nodeById.get("Central Node");
        const centerX = centralNode.x;
        const centerY = centralNode.y;
        d.vx += (centerX - d.x) * strength * alpha * widthHeightRatio;
        d.vy += (centerY - d.y) * strength * alpha;
      } else if (d.id.startsWith("Subnetwork-") && d.id.includes("Node")) {
        const parentId = d.id.split(" - ")[0];
        const parentNode = nodeById.get(parentId);
        d.vx += (parentNode.x - d.x) * strength * alpha * widthHeightRatio;
        d.vy += (parentNode.y - d.y) * strength * alpha;
      }
    });
  };
};

const SquishedBubbles = ({
  conversationIds,
  conversationToHighlights,
  parentTagsToHighlights,
  onNodeClick,
  selectedNode,
  selectedTag,
  setSelectedTag,
  initiallySelectedHighlight,
  onBackgroundClick,
  onScreensaverActiveChange, // new prop
}) => {
  const svgRef = useRef();
  const svgSelectionRef = useRef();
  const zoomBehaviorRef = useRef();
  const selectedNodeRef = useRef(null);
  const targetAlpha = 0.01;
  const simulationRef = useRef();
  const dataRef = useRef();
  const gCurvesRef = useRef();
  const nodesRef = useRef();
  const selectedTagRef = useRef(selectedTag);
  const screensaverTimeoutRef = useRef(null);
  const screensaverIntervalRef = useRef(null);
  const isScreensaverActiveRef = useRef(false);

  // Store the initial selection logic in a ref to prevent re-initialization
  const hasInitializedSelection = useRef(false);

  // Ref to manage simulation IDs
  const simulationIDRef = useRef(0);

  // Generate unique colors for parent tags using d3 color scale
  const parentTagColors = {};
  const uniqueParentTags = [...new Set(Object.keys(parentTagsToHighlights))];
  const colorScale = d3.scaleOrdinal(d3.schemeCategory10);
  uniqueParentTags.forEach((tag, i) => {
    parentTagColors[tag] = colorScale(i);
  });

  let svg;
  let gCurves;

  // Update the selected tag ref
  useEffect(() => {
    selectedTagRef.current = selectedTag;
  }, [selectedTag]);

  const handleNodeSelection = (
    node,
    data,
    gCurves,
    nodes,
    selectedTagRef,
    simulation,
    simulationIDRef,
    skipOnNodeClick = false
  ) => {
    if (!skipOnNodeClick) {
      onNodeClick(node.highlight);
    }
    showConnections(
      node,
      data,
      gCurves,
      nodes,
      selectedTagRef,
      simulationIDRef
    );
    selectedNodeRef.current = node;
    simulation.alpha(0).stop(); // Stop the simulation immediately

    // Only attempt to center if we have a valid SVG selection and zoom behavior
    if (svgSelectionRef.current && zoomBehaviorRef.current) {
      const width = svgSelectionRef.current.node().parentNode.clientWidth;
      const height = svgSelectionRef.current.node().parentNode.clientHeight;

      // Calculate new transform that centers the node
      const transform = d3.zoomIdentity
        .translate(width / 2, height / 2)
        .scale(1.5)
        .translate(-node.x, -node.y);

      // Apply the transform with a smooth transition
      svgSelectionRef.current
        .transition()
        .duration(1000)
        .call(zoomBehaviorRef.current.transform, transform);
    }
  };

  useEffect(() => {
    if (!simulationRef.current) {
      const data = generateNodesAndLinks(
        conversationIds,
        conversationToHighlights
      );
      const svgSelection = d3.select(svgRef.current);
      svgSelectionRef.current = svgSelection;

      // Remove any existing nodes or links
      svgSelection.selectAll("*").remove();

      const width = svgSelection.node().parentNode.clientWidth;
      const height = svgSelection.node().parentNode.clientHeight;
      svgSelection
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("viewBox", `0 0 ${width} ${height}`)
        .style("background-color", "black");

      // Create a group for all visualization elements that will be transformed
      const visualizationGroup = svgSelection.append("g")
        .attr("class", "visualization-group");

      // Basic zoom behavior - let D3 handle all the pan/zoom interactions
      const zoom = d3.zoom()
        .scaleExtent([0.5, 10])
        .on("zoom", (event) => {
          visualizationGroup.attr("transform", event.transform);
        });

      // Store the zoom behavior in the ref
      zoomBehaviorRef.current = zoom;

      // Apply zoom behavior to the SVG without any initial transform
      svgSelection.call(zoom);

      initializeNodePositions(data.nodes, width, height);

      const hullsGroup = visualizationGroup.append("g").attr("class", "hulls");
      gCurves = visualizationGroup.append("g").attr("class", "curves");

      const ringsGroup = visualizationGroup.append("g").attr("class", "rings");

      const nodes = visualizationGroup
        .append("g")
        .attr("class", "nodes")
        .selectAll("circle")
        .data(data.nodes.filter((d) => d.highlight !== undefined))
        .enter()
        .append("circle")
        .attr("r", NODE_RADIUS)
        .attr("fill", (d) => (d.highlight ? d.highlight.color : "black"))
        .on("click", (event, d) => {
          event.stopPropagation();
          handleNodeSelection(
            d,
            data,
            gCurves,
            nodes,
            selectedTagRef,
            simulationRef.current,
            simulationIDRef,
            false
          );
        })
        .call(
          d3
            .drag()
            .on("start", (event, d) => {
              if (selectedNodeRef.current) return;
              if (!event.active)
                simulationRef.current.alphaTarget(0.3).restart();
              d.fx = d.x;
              d.fy = d.y;
            })
            .on("drag", (event, d) => {
              if (selectedNodeRef.current) return;
              d.fx = event.x;
              d.fy = event.y;
            })
            .on("end", (event, d) => {
              if (selectedNodeRef.current) return;
              if (!event.active) simulationRef.current.alphaTarget(targetAlpha);
              d.fx = null;
              d.fy = null;
            })
        );

      // Store variables in refs for access in other hooks
      dataRef.current = data;
      gCurvesRef.current = gCurves;
      nodesRef.current = nodes;

      const networkLabelsGroup = visualizationGroup
        .append("g")
        .attr("class", "conversation-labels");

      const centralNodes = new Set(
        data.nodes
          .filter((d) => /^Central Node|^Subnetwork-\d+$/.test(d.id))
          .map((d) => d.id)
      );

      const simulation = d3
        .forceSimulation(data.nodes)
        .force(
          "link",
          d3
            .forceLink(data.links)
            .id((d) => d.id)
            .distance(10)
        )
        .force(
          "charge",
          d3
            .forceManyBody()
            .strength((d) =>
              centralNodes.has(d.id)
                ? 0
                : Math.sqrt(400 / conversationIds.length) * -10
            )
        )
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collide", d3.forceCollide().radius((d) =>
          /^Central Node|^Subnetwork-\d+$/.test(d.id) ? 0 : 8 //Collision radius for central nodes is made 0 to hide them in the visualization
        ))
        .force(
          "pull",
          forcePullToSubnetworkCenter(data.nodes, 0.05, width, height)
        )
        .alpha(1)
        .alphaDecay(0.02)
        .alphaTarget(targetAlpha);

      simulation.on("tick", () => {
        nodes.attr("cx", (d) => d.x).attr("cy", (d) => d.y);

        // Update ring positions
        ringsGroup.selectAll("circle").each(function (d) {
          const ring = d3.select(this);
          const nodeId = ring.attr("class").split("-")[1];
          const node = data.nodes.find((n) => n.id === nodeId);
          if (node) {
            ring.attr("cx", node.x).attr("cy", node.y);
          }
        });

        // Draw network outline for each subnetwork
        const subnetworks = {};
        data.nodes.forEach((d) => {
          const match = d.id.match(/^Subnetwork-(\d+)/);
          if (match) {
            const subnetworkId = match[0];
            if (!subnetworks[subnetworkId]) {
              subnetworks[subnetworkId] = [];
            }
            subnetworks[subnetworkId].push(d);
          }
        });

        hullsGroup.selectAll("*").remove();
        Object.keys(subnetworks).forEach((subnetworkId) => {
          const subnetworkNodes = subnetworks[subnetworkId];
          drawNetworkOutline(
            hullsGroup,
            networkLabelsGroup,
            subnetworkNodes,
            subnetworkId,
            null,
            false
          );
        });
      });

      simulationRef.current = simulation;

      // Periodically restart the simulation to keep the nodes moving
      const interval = setInterval(() => {
        if (!selectedNodeRef.current) {
          simulationRef.current.alpha(targetAlpha).restart();
        }
      }, 5000);

      svgSelection.on("click", (event) => {
        // Only handle clicks if we're not clicking on a node
        if (!event.target.classList.contains("nodes")) {
          onBackgroundClick();
          console.log("medley playback stopped");
          // Increment simulation ID to cancel previous simulations
          simulationIDRef.current += 1;

          selectedNodeRef.current = null;
          onNodeClick(null);
          // Clear connections and reset node and link styles
          gCurves.selectAll(".curved-lines").remove();

          nodes
            .transition()
            .duration(500)
            .attr("r", NODE_RADIUS)
            .attr("opacity", NODE_OPACITY);

          // Restart simulation with target alpha
          simulationRef.current.alpha(targetAlpha).restart();
        }
      });

      // Autoconnect highlights if provided
      if (initiallySelectedHighlight) {
        let index = 0;
        const runHighlightCycle = () => {
          if (index >= 1) {
            // If we've reached the end of the array, reset all
            gCurves.selectAll(".curved-lines").remove();
            nodes
              .transition()
              .duration(500)
              .attr("r", NODE_RADIUS)
              .attr("opacity", NODE_OPACITY);
            simulationRef.current.alpha(targetAlpha).restart();
            return;
          }

          const highlightId = initiallySelectedHighlight;
          const node = data.nodes.find(
            (n) => n.highlight && n.highlight.id === highlightId
          );
          if (node) {
            if (selectedNodeRef.current) {
              // Increment simulation ID to cancel previous simulations
              simulationIDRef.current += 1;

              gCurves.selectAll(".curved-lines").remove();
              nodes
                .transition()
                .duration(500)
                .attr("r", NODE_RADIUS)
                .attr("opacity", NODE_OPACITY);
            }
            // Restart simulation to allow nodes to move
            simulationRef.current.alpha(1).restart();
            setTimeout(() => {
              // Stop simulation and show connections after 2 seconds
              simulationRef.current.alpha(0).stop();
              handleNodeSelection(
                node,
                data,
                gCurves,
                nodes,
                selectedTagRef,
                simulationRef.current,
                simulationIDRef,
                true
              );
            }, 2000); // Allow nodes to move for 2 seconds
            setTimeout(() => {
              // Apply zoom to the selected node
              const transform = d3.zoomIdentity
                .translate(width / 2 - node.x, height / 2 - node.y)
                .scale(2);
              svgSelection.transition()
                .duration(1000)
                .call(zoomBehaviorRef.current.transform, transform);

              index++;
              setTimeout(runHighlightCycle, 5000);
            }, 1000);
          } else {
            console.warn(`Highlight ${highlightId} not found`);
            simulationRef.current.alphaTarget(targetAlpha).restart();
            index++;
            setTimeout(runHighlightCycle, 5000); // Schedule next execution
          }
        };

        // Start the cycle
        setTimeout(runHighlightCycle, 5000);
      }

      if (initiallySelectedHighlight && !hasInitializedSelection.current) {
        const node = data.nodes.find(
          (n) => n.highlight && n.highlight.id === initiallySelectedHighlight
        );
        if (node) {
          handleNodeSelection(
            node,
            data,
            gCurves,
            nodes,
            selectedTagRef,
            simulationRef.current,
            simulationIDRef,
            true
          );
          hasInitializedSelection.current = true;
        }
      }

      // Clear interval on unmount
      return () => clearInterval(interval);
    }
  }, [conversationIds, conversationToHighlights]);

  useEffect(() => {
    if (simulationRef.current && dataRef.current) {
      if (initiallySelectedHighlight) {
        const node = dataRef.current.nodes.find(
          (n) => n.highlight && n.highlight.id === initiallySelectedHighlight
        );
        if (node) {
          handleNodeSelection(
            node,
            dataRef.current,
            gCurvesRef.current,
            nodesRef.current,
            selectedTagRef,
            simulationRef.current,
            simulationIDRef,
            true
          );
        }
      } else {
        // Increment simulation ID to cancel previous simulations
        simulationIDRef.current += 1;

        selectedNodeRef.current = null;
        onNodeClick(null);
        // Clear connections and reset node and link styles
        gCurvesRef.current.selectAll(".curved-lines").remove();

        nodesRef.current
          .transition()
          .duration(500)
          .attr("r", NODE_RADIUS)
          .attr("opacity", NODE_OPACITY);

        simulationRef.current.alpha(1).restart();
      }
    }
  }, [initiallySelectedHighlight]);

  useEffect(() => {
    const highlightNodes = d3.select(svgRef.current).selectAll("circle");
    const curves = d3.select(svgRef.current).selectAll(".curved-lines path");

    if (selectedTag) {
      // Highlight nodes based on selected tag
      highlightNodes
        .transition()
        .attr("opacity", (d) =>
          nodeHasTag(d, selectedTag) ? NODE_OPACITY : NODE_HIGHLIGHT_OPACITY
        );

      // Update curves based on selected tag
      curves.transition().each(function (d) {
        const style = getCurveStyle(d, selectedTag);
        d3.select(this)
          .attr("stroke", style.stroke)
          .attr("opacity", style.opacity)
          .attr("stroke-width", style.strokeWidth);
      });
    } else {

      // Restore node appearance to 'selected node' state if a node is selected
      if (selectedNodeRef.current) {
        highlightNodes
          .transition()
          .attr("opacity", (d) =>
            d.id === selectedNodeRef.current.id ||
            nodesShareTag(selectedNodeRef.current, d)
              ? NODE_OPACITY
              : FADED_NODE_OPACITY
          )
          .attr("r", (d) =>
            d.id === selectedNodeRef.current.id ||
            nodesShareTag(selectedNodeRef.current, d)
              ? SELECTED_NODE_RADIUS
              : NODE_RADIUS
          );
      } 

      // Use the default faded style for all curves
      curves.transition().each(function () {
        d3.select(this)
          .attr("stroke", "white")
          .attr("opacity", 0.3)
          .attr("stroke-width", 1);
      });
    }
  }, [selectedTag]);

  // Add screensaver functionality
  const startScreensaver = useCallback(() => {
    if (isScreensaverActiveRef.current) return;

    isScreensaverActiveRef.current = true;
    if (onScreensaverActiveChange) onScreensaverActiveChange(true);
    const nodes = dataRef.current.nodes.filter(
      (d) => d.highlight !== undefined
    );

    const clearSelection = () => {
      selectedNodeRef.current = null;
      if (nodesRef.current) {
        nodesRef.current
          .transition()
          .duration(500)
          .attr("r", NODE_RADIUS)
          .attr("opacity", NODE_OPACITY);
      }
      selectedTagRef.current = null;
      onNodeClick(null);
      if (gCurvesRef.current) {
        gCurvesRef.current.selectAll(".curved-lines").remove();
        gCurvesRef.current.selectAll(".ring").remove();
        gCurvesRef.current.selectAll(".ripple").remove();
      }
      // Clear all rings
      d3.select(svgRef.current).select(".rings").selectAll("circle").remove();
      if (simulationRef.current) {
        simulationRef.current.alpha(targetAlpha).restart();
      }
    };

    let isShowingNode = false;

    const screensaverStep = () => {
      if (!isScreensaverActiveRef.current) return;

      if (!isShowingNode) {
        // Select a random node
        const randomIndex = Math.floor(Math.random() * nodes.length);
        const randomNode = nodes[randomIndex];

        // Select a random tag from this node
        const nodeTags = randomNode.highlight.tags || [];
        if (nodeTags.length > 0) {
          const randomTagIndex = Math.floor(Math.random() * nodeTags.length);
          const randomTag = nodeTags[randomTagIndex];
          selectedTagRef.current = randomTag;

          handleNodeSelection(
            randomNode,
            dataRef.current,
            gCurvesRef.current,
            nodesRef.current,
            selectedTagRef,
            simulationRef.current,
            simulationIDRef,
            false
          );

          // Draw rings for all nodes
          const ringsGroup = d3.select(svgRef.current).select(".rings");
          const targetNodes = dataRef.current.nodes.filter(
            (n) => n.highlight && nodesShareTag(randomNode, n)
          );

          dataRef.current.nodes.forEach((node) => {
            if (node.highlight) {
              const parentTagCounts = {};
              node.highlight.tags.forEach((tag) => {
                const parentTag = tag.split(" :: ")[0];
                parentTagCounts[parentTag] =
                  (parentTagCounts[parentTag] || 0) + 1;
              });

              // Calculate the opacity based on connection to selected node
              const nodeOpacity =
                node.id === randomNode.id || targetNodes.includes(node)
                  ? nodeHasTag(node, selectedTagRef.current)
                    ? NODE_OPACITY
                    : FADED_NODE_OPACITY
                  : FADED_NODE_OPACITY;

              // Start from the node radius
              let currentRadius = NODE_RADIUS;

              Object.entries(parentTagCounts).forEach(
                ([parentTag, count], index) => {
                  const thickness = Math.min(1, count) * 5;

                  ringsGroup
                    .append("circle")
                    .attr("class", `ring-${node.id}`)
                    .attr("cx", node.x)
                    .attr("cy", node.y)
                    .attr("r", currentRadius)
                    .attr("fill", "none")
                    .attr("stroke", parentTagColors[parentTag])
                    .attr("stroke-width", thickness)
                    .attr("opacity", nodeOpacity);

                  // Increment radius by thickness for next ring
                  currentRadius += thickness;
                }
              );
            }
          });

          isShowingNode = true;
        }

        // Schedule next step in 10 seconds
        setTimeout(screensaverStep, 10000);
      } else {
        // Clear the current selection
        clearSelection();
        isShowingNode = false;

        // Schedule next step in 3 second
        setTimeout(screensaverStep, 3000);
      }
    };

    // Start the sequence
    screensaverStep();
  }, [targetAlpha]);
  const stopScreensaver = useCallback(() => {
    if (!isScreensaverActiveRef.current) return;

    isScreensaverActiveRef.current = false;
    if (onScreensaverActiveChange) onScreensaverActiveChange(false);
    if (screensaverIntervalRef.current) {
      clearInterval(screensaverIntervalRef.current);
      screensaverIntervalRef.current = null;
    }
    if (screensaverTimeoutRef.current) {
      clearTimeout(screensaverTimeoutRef.current);
      screensaverTimeoutRef.current = null;
    }

    // Clear any selected node
    selectedNodeRef.current = null;
    if (nodesRef.current) {
      nodesRef.current
        .transition()
        .duration(500)
        .attr("r", NODE_RADIUS)
        .attr("opacity", NODE_OPACITY);
    }

    // Remove all rings
    d3.select(svgRef.current).select(".rings").selectAll("circle").remove();

    // Remove any other visual elements
    if (gCurvesRef.current) {
      gCurvesRef.current.selectAll(".curved-lines").remove();
      gCurvesRef.current.selectAll(".ring").remove();
      gCurvesRef.current.selectAll(".ripple").remove();
    }
    selectedTagRef.current = null;
    onNodeClick(null);
    if (simulationRef.current) {
      simulationRef.current.alpha(targetAlpha).restart();
    }
  }, [targetAlpha]);

  const resetScreensaverTimer = useCallback(() => {
    if (screensaverTimeoutRef.current) {
      clearTimeout(screensaverTimeoutRef.current);
    }
    // Schedule the screensaver to start in 5 minutes
    screensaverTimeoutRef.current = setTimeout(startScreensaver, 300000);
  }, [startScreensaver]);

  // Set up mouse movement detection
  useEffect(() => {
    const handleMouseMove = () => {
      if (isScreensaverActiveRef.current) {
        stopScreensaver();
      }
      resetScreensaverTimer();
    };

    window.addEventListener("mousemove", handleMouseMove);
    resetScreensaverTimer(); // Start initial timer

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      stopScreensaver();
    };
  }, [resetScreensaverTimer, stopScreensaver]);

  // Clean up intervals and timeouts
  useEffect(() => {
    return () => {
      if (screensaverIntervalRef.current) {
        clearInterval(screensaverIntervalRef.current);
      }
      if (screensaverTimeoutRef.current) {
        clearTimeout(screensaverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const svgSelection = d3.select(svgRef.current);
    function resize() {
      if (!svgRef.current) return;
      const width = svgRef.current.parentNode.clientWidth;
      const height = svgRef.current.parentNode.clientHeight;
      svgSelection
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', `0 0 ${width} ${height}`);
    }
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return <svg ref={svgRef}></svg>;
};

export default SquishedBubbles;
